import * as React from "react";
// import styled from "styled-components";

import * as releaseAdmin from "../../sdk/release-admin";
import { SEARCH_PAGE_SIZE } from "../../sdk/search";

import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Button from "@material-ui/core/Button";
import { ReleaseGrid } from "../../controllers/releases/release-grid";
import { ReleaseIndexSchema } from "../../../../../services/search-admin/types/index-schema/release-index-schema";
import { useHistory, useParams } from "react-router";
import { routes } from "../../routes";
import { BackofficeUser } from "../../../../../services/release-admin/types/backoffice-user";
import { BackofficeUserTier } from "../../../../../services/release-admin/types/backoffice-user-tier";
import { ReleaseDetailChangelog } from "../../controllers/releases/detail/detail-changelog";
import { ReleaseChangelog } from "../../../../../services/release-admin/types/release-changelog";

interface AgentViewPageProps {
  tier?: BackofficeUserTier;
}

export const AgentViewPage: React.FC<AgentViewPageProps> = ({ tier }) => {
  const history = useHistory();

  const { agentId } = useParams<{ agentId: string }>();

  const [loadingAssign, setLoadingAssign] = React.useState(false);
  const [assignmentList, setAssignmentList] = React.useState<string[]>([]);
  const [updateDateMap, setUpdateDateMap] = React.useState<
    Record<string, Date>
  >({});
  const [changelog, setChangelog] = React.useState<ReleaseChangelog[]>([]);
  const [changelogReleases, setChangelogReleases] = React.useState<
    ReleaseIndexSchema[]
  >([]);
  const [page, setPage] = React.useState(0);

  const [releases, setReleases] = React.useState<ReleaseIndexSchema[]>([]);

  const [currentView, setCurrentView] = React.useState<
    "assigned" | "changelog"
  >("changelog");

  const [users, setUsers] = React.useState<BackofficeUser[]>([]);
  const [agentName, setAgentName] = React.useState("");

  React.useEffect(() => {
    releaseAdmin.listUsers().then(async (response) => {
      setUsers(response.results.filter((u) => !!u.name));
    });
  }, [agentId]);

  React.useEffect(() => {
    if (agentId) {
      setLoadingAssign(true);
      releaseAdmin
        .agentView(agentId)
        .then((res) => {
          setAssignmentList(res.results.map((r) => r.id));
          setUpdateDateMap(
            res.results.reduce(
              (memo: Record<string, Date>, rel) => ({
                ...memo,
                [rel.id]: new Date(rel.updated_at),
              }),
              {}
            )
          );
        })
        .finally(() => setLoadingAssign(false));
    }
  }, [agentId, currentView]);

  React.useEffect(() => {
    const newUser = users.find((u) => u.pk === agentId);
    if (newUser) {
      setAgentName(newUser.name || "");
      releaseAdmin.agentChangelog(newUser.email).then(async (res) => {
        setChangelog(res.changelogs);
        setChangelogReleases(res.releases);
      });
    }
  }, [agentId, users]);

  React.useEffect(() => {
    if (assignmentList.length) {
      setLoadingAssign(true);
      releaseAdmin
        .agentView(agentId)
        .then((res) => {
          const sorted =
            res.results?.sort(
              (a, b) =>
                Number(updateDateMap[b.id] || 0) -
                Number(updateDateMap[a.id] || 0)
            ) ?? [];
          setReleases(sorted);
        })
        .finally(() => setLoadingAssign(false));
    }
  }, [assignmentList, updateDateMap, agentId]);

  if (!["tier-3", "tier-2", "tier-0"].includes(tier as string)) {
    return null;
  }

  return (
    <section>
      <Toolbar
        variant="dense"
        style={{ marginTop: "20px", display: "flex", flexWrap: "wrap" }}
      >
        <Typography variant="h4" style={{ flexGrow: 1 }}>
          {agentName || "Agent View"}
        </Typography>
        <FormControl>
          <InputLabel id="user-list-label">Select Agent</InputLabel>
          <Select
            labelId="user-list-label"
            id="user-list-select"
            style={{ width: "300px" }}
            value={agentId || ""}
            label="Select User/Group"
            onChange={(ev) =>
              history.push(`${routes.inbox.agentView}/${ev.target.value}`)
            }
          >
            {users.map((user) => (
              <MenuItem
                key={user.pk}
                value={user.pk}
              >{`${user.name} (${user.tier})`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <ButtonGroup
          variant="outlined"
          aria-label="outlined button group"
          style={{ marginLeft: "1rem" }}
        >
          <Button
            variant={currentView === "assigned" ? "contained" : "outlined"}
            onClick={() => setCurrentView("assigned")}
          >
            Inbox
          </Button>
          <Button
            variant={currentView === "changelog" ? "contained" : "outlined"}
            onClick={() => setCurrentView("changelog")}
          >
            Activity
          </Button>
        </ButtonGroup>
      </Toolbar>
      <div style={{ display: "flex", height: "700px", marginTop: "20px" }}>
        {currentView === "assigned" ? (
          <ReleaseGrid
            releases={releases.slice(
              page * SEARCH_PAGE_SIZE,
              (page + 1) * SEARCH_PAGE_SIZE
            )}
            size={releases.length}
            page={page}
            handlePageChange={setPage}
            loading={loadingAssign}
            handleClick={(release, ev) => {
              const url = `${routes.releases.list}/${release.id}`;
              if (ev.ctrlKey || ev.metaKey) {
                window.open(url, "_blank");
              } else {
                history.push(url, { from: "inbox" });
              }
            }}
          />
        ) : (
          <ReleaseDetailChangelog
            changelog={changelog}
            showDetails
            releases={changelogReleases}
          />
        )}
      </div>
    </section>
  );
};
